import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { profilesApi } from 'services/apiDigitalTicket/profiles';
import { benefitsApi } from 'services/apiDigitalTicket/benefits';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import CONST_PROFILE from 'constants/profile';

import * as ProfilesActions from './actions';
import { ProfilesTypes, ProfileInterface } from './types';

function* list({
  payload,
  meta,
}: ReturnType<typeof ProfilesActions.listProfilesRequest>): any {
  try {
    const { params, loading = true, filtering } = payload;

    if (loading && !filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando perfis' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(profilesApi.list, {
      params: {
        transportOperatorId: transportOperator.id,
        ...params,
      },
    });

    if (response?.status === 200) {
      yield put(
        ProfilesActions.listProfilesSuccess({
          profilesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield meta.promise.resolve(response);
      if (loading && !filtering) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - listProfiles - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProfilesActions.listProfilesFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

function* listLines({
  payload,
}: ReturnType<typeof ProfilesActions.listProfileLinesRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando linhas' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(benefitsApi.listLines, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        ProfilesActions.listProfileLinesSuccess({
          profileLines: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - listProfileLines - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProfilesActions.listProfileLinesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof ProfilesActions.filterProfilesRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(profilesApi.list, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        ProfilesActions.filterProfilesSuccess({
          filterProfilesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('profiles - filterProfiles - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProfilesActions.filterProfilesFailure());
    yield meta.promise.reject(error);
  }
}

function* get({
  payload,
}: ReturnType<typeof ProfilesActions.getProfileRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(profilesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        ProfilesActions.getProfileSuccess({ profileData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - getProfiles - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProfilesActions.getProfileFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getCid({
  payload,
}: ReturnType<typeof ProfilesActions.getCidRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Buscando CID' }));

    const response = yield call(profilesApi.getCid, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(ProfilesActions.getCidSuccess({ cid: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - getCid - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProfilesActions.getCidFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof ProfilesActions.createProfileRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando perfil' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(profilesApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        ProfilesActions.createProfileSuccess({
          profileCreated: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Perfil criado com sucesso',
        }),
      );

      yield put(ProfilesActions.setCurrentOperation({ operation: 'none' }));
      yield put(
        ProfilesActions.listProfilesRequest({
          params: {
            _order: 'ASC',
            _sort: 'order',
            _limit: 9999,
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - createProfile - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProfilesActions.createProfileFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof ProfilesActions.updateProfileRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(profilesApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DialogsActions.setClose());
      yield put(ProfilesActions.updateProfileSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Perfil alterado com sucesso',
        }),
      );

      yield put(ProfilesActions.setCurrentOperation({ operation: 'none' }));
      yield put(
        ProfilesActions.listProfilesRequest({
          params: {
            _order: 'ASC',
            _sort: 'order',
            _limit: 9999,
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - updateProfile - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type:
          internalCode === CONST_PROFILE.CANNOT_BE_UPDATED
            ? 'warning'
            : 'error',
        message,
      }),
    );
    yield put(ProfilesActions.updateProfileFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateOrder({
  payload,
}: ReturnType<typeof ProfilesActions.updateOrderRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando ordem dos itens' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const transformedPayload = payload.profiles.map(
      (profile: ProfileInterface) => ({
        profileId: profile.id,
        order: profile.order,
      }),
    );

    const response = yield call(profilesApi.updateOrder, {
      data: {
        transportOperatorId: transportOperator.id,
        profiles: transformedPayload,
      },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Ordem alterada com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(ProfilesActions.updateOrderSuccess());

      yield put(ProfilesActions.setCurrentOperation({ operation: 'none' }));
      yield put(
        ProfilesActions.listProfilesRequest({
          params: {
            _order: 'ASC',
            _sort: 'order',
            _limit: 9999,
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - updateOrder - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DialogsActions.setClose());
    yield put(ProfilesActions.updateOrderFailure());
    yield put(
      ProfilesActions.listProfilesRequest({
        params: {
          _order: 'ASC',
          _sort: 'order',
        },
      }),
    );
  }
}

function* remove({
  payload,
}: ReturnType<typeof ProfilesActions.deleteProfileRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo perfil' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(profilesApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Perfil excluído com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(ProfilesActions.deleteProfileSuccess());

      yield put(ProfilesActions.setCurrentOperation({ operation: 'none' }));
      yield put(
        ProfilesActions.listProfilesRequest({
          params: {
            _order: 'ASC',
            _sort: 'order',
            _limit: 9999,
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProfilesActions.deleteProfileFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-profile import/prefer-default-export
export const profilesSagas = [
  takeLatest(ProfilesTypes.LIST_PROFILES_REQUEST, list),
  takeLatest(ProfilesTypes.GET_CID_REQUEST, getCid),
  takeLatest(ProfilesTypes.LIST_PROFILE_LINES_REQUEST, listLines),
  takeLatest(ProfilesTypes.FILTER_PROFILES_REQUEST, filter),
  takeLatest(ProfilesTypes.GET_PROFILE_REQUEST, get),
  takeLatest(ProfilesTypes.CREATE_PROFILE_REQUEST, create),
  takeLatest(ProfilesTypes.UPDATE_PROFILE_REQUEST, update),
  takeLatest(ProfilesTypes.UPDATE_ORDER_PROFILE_REQUEST, updateOrder),
  takeLatest(ProfilesTypes.DELETE_PROFILE_REQUEST, remove),
];
